import { supabase } from "@/integrations/supabase/client";

export type RoleRecord = {
  user_id: string;
  role: string;
  email?: string | null;
};

const ROLE_SOURCES = [
  { table: "app_roles", select: "user_id, role, email" },
  { table: "user_roles", select: "user_id, role" },
] as const;

type RoleSource = (typeof ROLE_SOURCES)[number]["table"];

function isTableMissingError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const message = "message" in error ? String((error as any).message) : "";
  const details = "details" in error ? String((error as any).details) : "";
  return (
    message.includes("does not exist") ||
    message.includes("not found") ||
    details.includes("does not exist")
  );
}

async function detectRoleSource(): Promise<RoleSource | null> {
  for (const source of ROLE_SOURCES) {
    const { error } = await supabase
      .from(source.table as any)
      .select("user_id", { count: "exact", head: true })
      .limit(1);

    if (!error) {
      return source.table;
    }

    if (!isTableMissingError(error)) {
      // Table exists but other error (like permission). Prefer this source.
      return source.table;
    }
  }

  return null;
}

export async function fetchAllRoleRecords(): Promise<{
  source: RoleSource | null;
  records: RoleRecord[];
}> {
  const source = await detectRoleSource();
  if (!source) {
    return { source: null, records: [] };
  }

  const config = ROLE_SOURCES.find(item => item.table === source)!;
  const { data, error } = await supabase.from(source as any).select(config.select);

  if (error) {
    throw error;
  }

  const records = (data ?? []).map((item: any) => ({
    user_id: item.user_id,
    role: item.role,
    email: "email" in item ? item.email : null,
  }));

  return { source, records };
}

export async function fetchRolesForUser(userId: string): Promise<{
  source: RoleSource | null;
  roles: RoleRecord[];
}> {
  const source = await detectRoleSource();
  if (!source) {
    return { source: null, roles: [] };
  }

  const config = ROLE_SOURCES.find(item => item.table === source)!;
  const { data, error } = await supabase
    .from(source as any)
    .select(config.select)
    .eq("user_id", userId);

  if (error) {
    throw error;
  }

  const roles = (data ?? []).map((item: any) => ({
    user_id: item.user_id,
    role: item.role,
    email: "email" in item ? item.email : null,
  }));

  return { source, roles };
}

export async function upsertRolesForUser(
  userId: string,
  email: string,
  roles: string[],
): Promise<void> {
  const source = await detectRoleSource();
  if (!source) {
    throw new Error("Tabela de permissões não encontrada no Supabase");
  }

  const { error: deleteError } = await supabase
    .from(source as any)
    .delete()
    .eq("user_id", userId);

  if (deleteError) {
    throw deleteError;
  }

  if (!roles.length) {
    return;
  }

  if (source === "app_roles") {
    const payload = roles.map(role => ({
      user_id: userId,
      email,
      role,
    }));

    const { error } = await supabase
      .from("app_roles" as any)
      .insert(payload);

    if (error) {
      throw error;
    }
    return;
  }

  const payload = roles.map(role => ({
    user_id: userId,
    role,
  }));

  const { error } = await supabase
    .from("user_roles" as any)
    .insert(payload);

  if (error) {
    throw error;
  }
}

export async function ensureUserHasRole(
  userId: string,
  email: string,
  role: string,
): Promise<void> {
  const { source, roles } = await fetchRolesForUser(userId);

  if (roles.some(record => record.role === role)) {
    return;
  }

  const nextRoles = [...new Set([...roles.map(record => record.role), role])];
  await upsertRolesForUser(userId, email, nextRoles);
}

export async function isAdminUser(userId: string): Promise<boolean> {
  if (!userId) return false;

  try {
    const { data, error } = await supabase.rpc("is_admin", { u: userId });
    if (!error && typeof data === "boolean") {
      return data;
    }
  } catch (error) {
    console.warn("Não foi possível verificar admin via função is_admin", error);
  }

  try {
    const result = await fetchRolesForUser(userId);
    return result.roles.some(record => record.role === "admin" || record.role === "super_admin");
  } catch (error) {
    console.warn("Não foi possível verificar admin via tabelas de role", error);
    return false;
  }
}
